import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import { useEffect, useState } from "react";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import IconButton from "@mui/material/IconButton";
import { AddCircleOutline, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";

interface PageData {
	status_code: number
	status_text: string
	tlds: AllowedDomainTLD[]
	total_count: number
}

interface AllowedDomainTLD {
	id: number
	tld: string
	created_at: number
}

export default function AdminAllowedDomainTLDs() {
	const { enqueueSnackbar } = useSnackbar();

	const [pageData, setPageData] = useState<PageData>();
	const [actionsDisabled, setActionsDisabled] = useState(false);
	const [openAddDialog, setOpenAddDialog] = useState(false);

	// Form states
	const [newTLD, setNewTLD] = useState("");

	// Query for fetching domain TLD data
	const pageDataQuery = useQuery({
		queryKey: ["allowedDomainTLDsPageData"],
		queryFn: () => authenticateAndFetchData("/admin/domain-tlds/"),
		gcTime: 0,
		retry: retryFn,
		refetchOnWindowFocus: false,
	})

	useEffect(() => {
		if (pageDataQuery.data) {
			setPageData(pageDataQuery.data.data);
		}
	}, [pageDataQuery.data]);

	// Mutation for adding new domain TLD
	const addDomainTLDMutation = useMutation({
		mutationKey: ["addAllowedDomainTLD"],
		mutationFn: (data: { tld: string }) =>
			authenticateAndPostData("/admin/domain-tlds/add/", data),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			pageDataQuery.refetch().then();
			setOpenAddDialog(false);
			setNewTLD("");
			enqueueSnackbar("Domain TLD added successfully", { variant: "success" });
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, { variant: "error" });
		}
	});

	// Mutation for deleting domain TLD
	const deleteDomainTLDMutation = useMutation({
		mutationKey: ["deleteAllowedDomainTLD"],
		mutationFn: (id: number) => authenticateAndPostData("/admin/domain-tlds/delete/", { id }),
		gcTime: 0,
		retry: retryFn,
		onSuccess: () => {
			pageDataQuery.refetch().then();
			enqueueSnackbar("Domain TLD deleted successfully", { variant: "success" });
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, { variant: "error" });
		}
	});

	// Mutation for bulk adding popular domain TLDs
	const bulkAddPopularMutation = useMutation({
		mutationKey: ["bulkAddPopularDomainTLDs"],
		mutationFn: () => authenticateAndPostData("/admin/domain-tlds/bulk-add-popular/", {}),
		gcTime: 0,
		retry: retryFn,
		onSuccess: (response) => {
			pageDataQuery.refetch().then();
			enqueueSnackbar(response.data.message, { variant: "success" });
		},
		onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message, { variant: "error" });
		}
	});

	// Enable/disable actions
	useEffect(() => {
		setActionsDisabled(
			addDomainTLDMutation.isPending ||
			deleteDomainTLDMutation.isPending ||
			bulkAddPopularMutation.isPending
		)
	}, [addDomainTLDMutation.isPending, deleteDomainTLDMutation.isPending, bulkAddPopularMutation.isPending]);

	const columns: GridColDef<AllowedDomainTLD>[] = [
		{
			field: "tld",
			headerName: "TLD",
			flex: 1,
			minWidth: 120,
			hideable: false,
			editable: false,
			filterable: true,
			sortable: true,
			renderCell: (params) => (
				<Typography variant="body2" fontFamily="monospace">
					.{params.value}
				</Typography>
			)
		},
		{
			field: "created_at",
			headerName: "Created At",
			flex: 1,
			minWidth: 150,
			hideable: false,
			editable: false,
			filterable: true,
			sortable: true,
			renderCell: (params) => (
				<Typography variant="body2">
					{new Date(params.value).toLocaleDateString()}
				</Typography>
			)
		},
		{
			field: "actions",
			headerName: "Actions",
			filterable: false,
			sortable: false,
			hideable: false,
			editable: false,
			minWidth: 100,
			renderCell: (params) => (
				<IconButton
					color="error"
					size="small"
					onClick={() => handleDelete(params.row.id)}
					disabled={actionsDisabled}
				>
					<Delete />
				</IconButton>
			)
		}
	];

	function handleAdd() {
		setOpenAddDialog(true);
	}

	function handleDelete(id: number) {
		if (window.confirm("Are you sure you want to delete this domain TLD?")) {
			deleteDomainTLDMutation.mutate(id);
		}
	}

	function handleSubmitAdd() {
		if (!newTLD.trim()) {
			enqueueSnackbar("TLD cannot be empty", { variant: "error" });
			return;
		}
		addDomainTLDMutation.mutate({
			tld: newTLD.trim()
		});
	}

	function handleBulkAddPopular() {
		if (window.confirm("This will add popular domain TLDs like .com, .org, .net, etc. Continue?")) {
			bulkAddPopularMutation.mutate();
		}
	}

	// ============================================================
	// --------------------- MAIN RENDER CODE ---------------------
	// ============================================================

	if (pageDataQuery.isLoading) {
		return <PageLoading />
	}

	if (pageDataQuery.isError) {
		return <PageDataErrorHandler error={pageDataQuery.error} />
	}

	return (
		<Box>
			<Stack direction="column" spacing={3}>
				<Typography variant="h4">
					Manage Allowed Domain TLDs
				</Typography>

				{/* Statistics */}
				{pageData && (
					<Stack direction="row" spacing={2}>
						<Chip label={`Total: ${pageData.total_count}`} color="primary" />
					</Stack>
				)}

				{/* Action Buttons */}
				<Stack direction="row" spacing={2}>
					<Button
						variant="contained"
						startIcon={<AddCircleOutline />}
						onClick={handleAdd}
						disabled={actionsDisabled}
					>
						Add Domain TLD
					</Button>
					<Button
						variant="outlined"
						onClick={handleBulkAddPopular}
						disabled={actionsDisabled}
					>
						Bulk Add Popular Domain TLDs
					</Button>
				</Stack>

				<Divider />

				{/* Data Grid */}
				{pageData && (
					<DataGrid
						rows={pageData.tlds}
						columns={columns}
						checkboxSelection={false}
						disableRowSelectionOnClick
						autoHeight
						pageSizeOptions={[25, 50, 100]}
						initialState={{
							pagination: { paginationModel: { pageSize: 25 } },
						}}
					/>
				)}
			</Stack>

			{/* Add Domain TLD Dialog */}
			<Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Add New Domain TLD</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							label="Domain TLD"
							value={newTLD}
							onChange={(e) => setNewTLD(e.target.value)}
							placeholder="com, org, net, etc."
							helperText="Enter without the leading dot"
							fullWidth
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
					<Button
						onClick={handleSubmitAdd}
						variant="contained"
						disabled={addDomainTLDMutation.isPending}
					>
						Add Domain TLD
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
